

import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Grid,
  Autocomplete,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Chip,
  Tooltip,
  useMediaQuery,
  useTheme,
  Collapse,
} from "@mui/material";
import {
  Description as FileIcon, ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  getIssues,
  exportDivisionWiseIssue,
} from "../../../Services/Services";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InfiniteScroll from "react-infinite-scroll-component";
import { useLocation } from "react-router-dom";

function Department_wise_material_usage() {
  // ========== States ==========
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedIssue, setExpandedIssue] = useState(null);
  const [issues, setIssues] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [divisionLists, setDivisionLists] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);

  // Date Range States
  const [dateRange, setDateRange] = useState("today");
  const [customDateRange, setCustomDateRange] = useState({ from: "", to: "" });
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [tempCustomDateRange, setTempCustomDateRange] = useState({ from: "", to: "" });

  const location = useLocation();

  // ========== Date Helper Functions ==========
  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    switch (rangeType) {
      case "today":
        return formatDate(now);
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;
      default:
        return "";
    }
  };

  // ========== Fetch Items ==========
  const fetchItems = async (pageNumber = 1, append = false) => {
    if (!selectedDivision) {
      setIssues([]);
      return;
    }

    setLoading(true);
    try {
      const filters = {};

      if (selectedDivision?.division_id) {
        filters.division_id = selectedDivision.division_id;
      }

      filters.date_range = dateRange;

      if (
        dateRange === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        filters.from_date = customDateRange.from;
        filters.to_date = customDateRange.to;
      }

      console.log("📤 Department Usage API Params:", filters);

      const data = await getIssues(pageNumber, filters);

      if (append) {
        setIssues((prev) => [...prev, ...(data.results || [])]);
      } else {
        setIssues(data.results || []);
      }

      setHasMore(!!data.next);
      setPage(pageNumber);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // ========== Excel Export Handler ==========
  const handleExcelExport = async () => {
    if (!selectedDivision) {
      toast.warning("Please select a division first");
      return;
    }

    setExcelLoading(true);
    try {
      const filters = {
        division: selectedDivision.division_id,
        date_range: dateRange,
      };

      if (
        dateRange === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        filters.from_date = customDateRange.from;
        filters.to_date = customDateRange.to;
      }

      const blob = await exportDivisionWiseIssue(filters);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Department_Material_Usage_${selectedDivision.division
        }_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success("Excel downloaded successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export Excel");
    } finally {
      setExcelLoading(false);
    }
  };

  // ========== Filter Effect ==========
  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchItems(1, false);
    }, 500);

    return () => clearTimeout(handler);
  }, [selectedDivision, dateRange, customDateRange]);

  // ========== Infinite Scroll ==========
  const fetchMoreData = () => {
    if (hasMore && !loading) {
      fetchItems(page + 1, true);
    }
  };

  // ========== Reset Handler ==========
  const handleReset = () => {
    setSelectedDivision(null);
    setDateRange("today");
    setCustomDateRange({ from: "", to: "" });
    setTempCustomDateRange({ from: "", to: "" }); // ✅ Add
    setShowCustomDateInputs(false); // ✅ Add
    setIssues([]);
    setPage(1);
    setHasMore(true);

  };

  // ✅ Handle Custom Date Range Apply
  const handleApplyCustomDateRange = () => {
    if (tempCustomDateRange.from && tempCustomDateRange.to) {
      setCustomDateRange({
        from: tempCustomDateRange.from,
        to: tempCustomDateRange.to,
      });
      setDateRange("custom");
      setDateMenuAnchor(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };

  // ✅ Handle Cancel Custom Date Range
  const handleCancelCustomDateRange = () => {
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };
  // ========== Calculate Grand Total (Memoized) ==========
  const grandTotal = useMemo(() => {
    let totalQty = 0;
    let totalValue = 0;

    issues.forEach((issue) => {
      (issue.items || []).forEach((item) => {
        totalQty += item.issue_qty || 0;
        totalValue += (item.issue_qty || 0) * (item.rate || 0);
      });
    });

    return { totalQty, totalValue };
  }, [issues]);

  // ========== Calculate Division Subtotal ==========
  const calculateDivisionTotal = (items) => {
    let totalQty = 0;
    let totalValue = 0;

    items.forEach((item) => {
      totalQty += item.issue_qty || 0;
      totalValue += (item.issue_qty || 0) * (item.rate || 0);
    });

    return { totalQty, totalValue };
  };

  // ========== Total Row Count for Infinite Scroll ==========
  const totalRowCount = useMemo(() => {
    return issues.reduce((sum, issue) => sum + (issue.items?.length || 0), 0);
  }, [issues]);

  // ========== Cleanup ==========
  useEffect(() => {
    if (location.state?.edited) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);


  return (
    <Box sx={{ mt: -5, pt: -3, mb: 2, width: "100%", gap: 2 }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ========== FILTER SECTION ========== */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={8}>
          <TextField
            label="Date Range"
            value={
              dateRange === "custom" &&
                customDateRange.from &&
                customDateRange.to
                ? `${formatDateDisplay(
                  customDateRange.from
                )} to ${formatDateDisplay(customDateRange.to)}`
                : dateRange === "today"
                  ? "Today"
                  : dateRange === "yesterday"
                    ? "Yesterday"
                    : dateRange === "this_week"
                      ? "This Week"
                      : dateRange === "previous_week"
                        ? "Previous Week"
                        : dateRange === "this_month"
                          ? "This Month"
                          : dateRange === "current_year"
                            ? "Current Year"
                            : dateRange === "financial_year"
                              ? "Financial Year"
                              : "Select Date Range"
            }
            onClick={(e) => setDateMenuAnchor(e.currentTarget)}
            size="small"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton size="small" edge="end">
                  <ArrowDropDownIcon />
                </IconButton>
              ),
            }}
            sx={{
              cursor: "pointer",
              "& .MuiInputBase-root": { height: 35, borderRadius: 0 },
              "& .MuiInputLabel-root": { fontSize: "0.7rem" },
            }}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <Button
            variant="contained"
            onClick={handleReset}
            sx={{
              height: "35px",
              borderRadius: "0",
              fontSize: "0.7rem",
            }}
            fullWidth
          >
            Reset
          </Button>
        </Grid>

        <Grid item xs={12} sm={1}>
          <Tooltip
            title={excelLoading ? "Exporting..." : "Download Excel"}
            arrow
          >
            <span>
              <IconButton
                color="success"
                size="small"
                onClick={handleExcelExport}
                disabled={excelLoading || !selectedDivision}
                sx={{
                  borderRadius: "4px",
                  height: "35px",
                  width: "35px",
                }}
              >
                {excelLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <FileIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>

      {/* ========== DATE RANGE MENU - UPDATED ========== */}
      <Menu
        anchorEl={dateMenuAnchor}
        open={Boolean(dateMenuAnchor)}
        onClose={() => {
          setDateMenuAnchor(null);
          setShowCustomDateInputs(false);
          setTempCustomDateRange({ from: "", to: "" });
        }}
        PaperProps={{ sx: { width: 350, maxHeight: 500 } }}
      >
        {[
          { key: "today", label: "Today" },
          { key: "yesterday", label: "Yesterday" },
          { key: "this_week", label: "This Week" },
          { key: "previous_week", label: "Previous Week" },
          { key: "this_month", label: "This Month" },
          { key: "current_year", label: "Current Year" },
          { key: "financial_year", label: "Financial Year" },
        ].map((range) => (
          <MenuItem
            key={range.key}
            onClick={() => {
              setDateRange(range.key);
              setCustomDateRange({ from: "", to: "" });
              setDateMenuAnchor(null);
              setShowCustomDateInputs(false);
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography variant="body2">{range.label}</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.65rem" }}
              >
                {getDateRangeDisplay(range.key)}
              </Typography>
            </Box>
          </MenuItem>
        ))}

        <Divider />

        {/* ✅ Custom Date Range - Expandable */}
        <MenuItem
          onClick={() => setShowCustomDateInputs(!showCustomDateInputs)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: showCustomDateInputs ? "#e3f2fd" : "inherit",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: showCustomDateInputs ? 600 : 400 }}>
            Custom Date Range
          </Typography>
          {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
        </MenuItem>

        {/* ✅ Both Date Pickers Together */}
        <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 2,
              py: 2,
              bgcolor: "#f5f5f5",
              borderTop: "1px solid #e0e0e0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Select Date Range
            </Typography>

            {/* From and To Date Inputs Side by Side */}
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                label="From Date"
                type="date"
                size="small"
                value={tempCustomDateRange.from}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    from: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: tempCustomDateRange.to || new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />

              <TextField
                label="To Date"
                type="date"
                size="small"
                value={tempCustomDateRange.to}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    to: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: tempCustomDateRange.from || undefined,
                  max: new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </Box>

            {/* Selected Range Preview */}
            {tempCustomDateRange.from && tempCustomDateRange.to && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1.5,
                  p: 1,
                  bgcolor: "#e8f5e9",
                  borderRadius: 1,
                  textAlign: "center",
                  color: "#2e7d32",
                  fontWeight: 500,
                }}
              >
                📅 {formatDateDisplay(tempCustomDateRange.from)} to{" "}
                {formatDateDisplay(tempCustomDateRange.to)}
              </Typography>
            )}

            {/* Apply and Cancel Buttons */}
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelCustomDateRange}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyCustomDateRange}
                disabled={!tempCustomDateRange.from || !tempCustomDateRange.to}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Menu>

      {/* ✅ Mobile/Desktop Conditional Rendering */}
      <InfiniteScroll
        dataLength={totalRowCount}
        next={fetchMoreData}
        hasMore={hasMore}
        height={"72vh"}
        loader={
          loading && (
            <Box sx={{ textAlign: "center", p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )
        }
        endMessage={
          selectedDivision &&
          issues.length > 0 && (
            <Typography
              sx={{ textAlign: "center", p: 2, color: "text.secondary" }}
            >
              No more data
            </Typography>
          )
        }
      >
        {isMobile ? (
          /* 📱 MOBILE VIEW - Collapsible Cards */
          <Box sx={{ bgcolor: "background.paper" }}>
            {!selectedDivision ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="body1" color="text.secondary">
                  Please select a division
                </Typography>
              </Box>
            ) : loading && issues.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Loading data...
                </Typography>
              </Box>
            ) : issues.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography color="text.secondary">No Data Found</Typography>
              </Box>
            ) : (
              (() => {
                let divisionSerialNo = 0;

                return issues.map((issue) => {
                  const items = issue.items || [];
                  divisionSerialNo++;

                  return (
                    <Box key={issue.id}>
                      {/* Issue Header Card */}
                      <Box
                        onClick={() =>
                          setExpandedIssue(
                            expandedIssue === issue.slip_no
                              ? null
                              : issue.slip_no
                          )
                        }
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 1.5,
                          cursor: "pointer",
                          bgcolor:
                            divisionSerialNo % 2 === 0 ? "#fafafa" : "#ffffff",
                          "&:active": { bgcolor: "#e3f2fd" },
                          borderLeft: "4px solid #2196f3",
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                color: "#666",
                                minWidth: 25,
                              }}
                            >
                              #{divisionSerialNo}
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: "bold",
                                fontSize: "0.85rem",
                                flex: 1,
                              }}
                            >
                              {issue.slip_no}
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: "#666",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <span style={{ fontWeight: 500 }}>
                              {issue.project_name || "N/A"}
                            </span>
                            <span style={{ color: "#999" }}>•</span>
                            <span style={{ fontSize: "0.7rem" }}>
                              {items.length} item{items.length !== 1 ? "s" : ""}
                            </span>
                          </Typography>
                        </Box>
                        <IconButton size="small" sx={{ ml: 1 }}>
                          {expandedIssue === issue.slip_no ? (
                            <ExpandLess fontSize="small" />
                          ) : (
                            <ExpandMore fontSize="small" />
                          )}
                        </IconButton>
                      </Box>

                      {/* Issue Details - Collapsible */}
                      <Collapse
                        in={expandedIssue === issue.slip_no}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box
                          sx={{
                            px: 1.5,
                            py: 1,
                            bgcolor:
                              divisionSerialNo % 2 === 0
                                ? "#f8f9fa"
                                : "#fafafa",
                            borderLeft: "4px solid #2196f3",
                          }}
                        >
                          {/* Issue Level Info */}
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 0.8,
                              mb: 1.5,
                              pb: 1,
                              borderBottom: "2px solid #e0e0e0",
                            }}
                          >
                            <Box sx={{ gridColumn: "1 / -1" }}>
                              <Typography
                                sx={{
                                  fontSize: "0.6rem",
                                  color: "#999",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                REQ. SLIP NO
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  color: "#1976d2",
                                }}
                              >
                                {issue.slip_no}
                              </Typography>
                            </Box>

                            <Box sx={{ gridColumn: "1 / -1" }}>
                              <Typography
                                sx={{
                                  fontSize: "0.6rem",
                                  color: "#999",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                PROJECT
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  fontWeight: 500,
                                  color: "#555",
                                }}
                              >
                                {issue.project_name || "-"}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography
                                sx={{
                                  fontSize: "0.6rem",
                                  color: "#999",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                TOTAL ITEMS
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  fontWeight: 600,
                                  color: "#4caf50",
                                }}
                              >
                                {items.length}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Items List */}
                          <Typography
                            sx={{
                              fontSize: "0.65rem",
                              fontWeight: 600,
                              color: "#666",
                              mb: 1,
                              letterSpacing: "0.5px",
                            }}
                          >
                            ITEMS ({items.length})
                          </Typography>

                          {items.map((item, itemIndex) => (
                            <Box
                              key={itemIndex}
                              sx={{
                                mb: 1.5,
                                p: 1,
                                bgcolor: "#fff",
                                borderRadius: "4px",
                                border: "1px solid #e0e0e0",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  color: "#1976d2",
                                  mb: 0.8,
                                }}
                              >
                                Item #{itemIndex + 1}
                              </Typography>

                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: 0.8,
                                }}
                              >
                                <Box sx={{ gridColumn: "1 / -1" }}>
                                  <Typography
                                    sx={{
                                      fontSize: "0.6rem",
                                      color: "#999",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    ITEM NAME
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.72rem",
                                      fontWeight: 500,
                                      color: "#333",
                                    }}
                                  >
                                    {item.item_name || "-"}
                                  </Typography>
                                </Box>

                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "0.6rem",
                                      color: "#999",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    CATEGORY
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.7rem",
                                      fontWeight: 500,
                                      color: "#555",
                                    }}
                                  >
                                    {item.category_name || "-"}
                                  </Typography>
                                </Box>

                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "0.6rem",
                                      color: "#999",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    SUB CATEGORY
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.7rem",
                                      fontWeight: 500,
                                      color: "#555",
                                    }}
                                  >
                                    {item.sub_category_name || "-"}
                                  </Typography>
                                </Box>

                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "0.6rem",
                                      color: "#999",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    ITEM TYPE
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.7rem",
                                      fontWeight: 500,
                                      color: "#555",
                                    }}
                                  >
                                    {item.item_type || "-"}
                                  </Typography>
                                </Box>

                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "0.6rem",
                                      color: "#999",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    ISSUE QTY
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.72rem",
                                      fontWeight: 600,
                                      color:
                                        item.issue_qty > 100
                                          ? "#f44336"
                                          : "#4caf50",
                                    }}
                                  >
                                    {item.issue_qty || 0}
                                  </Typography>
                                </Box>

                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "0.6rem",
                                      color: "#999",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    UNIT
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.72rem",
                                      fontWeight: 500,
                                      color: "#555",
                                    }}
                                  >
                                    {item.uom_name || "-"}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Collapse>
                      <Divider />
                    </Box>
                  );
                });
              })()
            )}
          </Box>
        ) : (
          /* 💻 DESKTOP VIEW - Original Table */
          <TableContainer
            component={Paper}
            id="item-table"
            sx={{
              maxHeight: "72vh",
              overflowX: { xs: "auto", sm: "unset" },
              overflowY: "auto",
              width: "100%",
            }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: "max-content",
                "& td, & th": {
                  borderBottom: "1px solid #ccc",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                },
              }}
            >
              {/* ========== TABLE HEAD ========== */}
              <TableHead
                sx={{
                  "& th": {
                    fontWeight: "bold",
                    color: (theme) =>
                      theme.palette.mode === "dark" ? "#2196f3" : "#333",
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "black" : "#f5f5f5",
                  },
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                      minWidth: 50,
                      padding: "8px",
                    }}
                  >
                    S. No.
                  </TableCell>
                  <TableCell>Req.Slip No</TableCell>
                  <TableCell>Product Type</TableCell>
                  <TableCell>Category</TableCell>
                  {/* <TableCell>Sub Category</TableCell> */}
                  <TableCell>Item Type</TableCell>
                  <TableCell>Item Name</TableCell>
                  <TableCell align="right">Issue Qty</TableCell>
                  <TableCell align="right">Unit</TableCell>
                </TableRow>
              </TableHead>

              {/* ========== TABLE BODY ========== */}
              <TableBody>
                {!selectedDivision ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                      {/* Empty state */}
                    </TableCell>
                  </TableRow>
                ) : loading && issues.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        Loading data...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : issues.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                      <Box>
                        <Typography color="text.secondary">
                          No Data Found
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {(() => {
                      let divisionSerialNo = 0;

                      return issues.flatMap((issue) => {
                        const items = issue.items || [];
                        divisionSerialNo++;

                        return items.map((item, itemIdx) => {
                          const isFirstItem = itemIdx === 0;

                          return (
                            <TableRow
                              key={`${issue.id}-${itemIdx}`}
                              hover
                              sx={{
                                backgroundColor:
                                  divisionSerialNo % 2 === 0
                                    ? "#fafafa"
                                    : "#ffffff",
                                "&:hover": {
                                  backgroundColor: "#e3f2fd",
                                  "& td": {
                                    backgroundColor: "#e3f2fd !important",
                                  },
                                },
                                "& td": {
                                  paddingTop: "4px !important",
                                  paddingBottom: "4px !important",
                                  fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                                  whiteSpace: "nowrap",
                                  color: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "white"
                                      : "inherit",
                                },
                              }}
                            >
                              {/* Serial Number - Rowspan */}
                              {isFirstItem && (
                                <TableCell
                                  rowSpan={items.length}
                                  align="center"
                                  sx={{
                                    position: "sticky",
                                    left: 0,
                                    backgroundColor:
                                      divisionSerialNo % 2 === 0
                                        ? "#fafafa"
                                        : "#ffffff",
                                    zIndex: 2,
                                    verticalAlign: "middle",
                                  }}
                                >
                                  {divisionSerialNo}
                                </TableCell>
                              )}

                              {/* Slip No - Rowspan */}
                              {isFirstItem && (
                                <TableCell
                                  rowSpan={items.length}
                                  sx={{ verticalAlign: "middle" }}
                                >
                                  {issue.slip_no}
                                </TableCell>
                              )}

                              {/* Project - Rowspan */}
                              {isFirstItem && (
                                <TableCell
                                  rowSpan={items.length}
                                  sx={{ verticalAlign: "middle" }}
                                >
                                  {issue.project_name}
                                </TableCell>
                              )}

                              {/* Item Details */}
                              <TableCell>{item.category_name || "-"}</TableCell>
                              <TableCell>
                                {item.sub_category_name || "-"}
                              </TableCell>
                              <TableCell>{item.item_type || "-"}</TableCell>
                              <TableCell
                                sx={{
                                  maxWidth: 200,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                <Tooltip title={item.item_name} arrow>
                                  <span>{item.item_name || "-"}</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontWeight: "500",
                                  color:
                                    item.issue_qty > 100
                                      ? "error.main"
                                      : "text.primary",
                                }}
                              >
                                {item.issue_qty || 0}
                              </TableCell>
                              <TableCell align="right">
                                {item.uom_name || "-"}
                              </TableCell>
                            </TableRow>
                          );
                        });
                      });
                    })()}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </InfiniteScroll>
    </Box>
  );
}

export default Department_wise_material_usage;